import { useEffect, useState, type RefObject } from 'react';

/**
 * HTML composites use vh/vw against the browser viewport (same as the fullscreen player).
 * Embedded previews shrink that layout by previewFrameHeight / viewportHeight.
 */
export function resolvePreviewStageViewportScale(frameHeightPx: number): number {
  if (frameHeightPx <= 0 || typeof window === 'undefined') {
    return 1;
  }
  const viewportHeight = window.innerHeight;
  if (viewportHeight <= 0) {
    return 1;
  }
  return frameHeightPx / viewportHeight;
}

export function usePreviewStageViewportScale(frameRef: RefObject<HTMLElement | null>): number {
  return usePreviewStageMetrics(frameRef).scale;
}

/** Stage (preview frame) height + viewport compensation scale for embedded HTML. */
export function usePreviewStageMetrics(frameRef: RefObject<HTMLElement | null>): {
  scale: number;
  stageHeightPx: number;
} {
  const [metrics, setMetrics] = useState({ scale: 1, stageHeightPx: 0 });

  useEffect(() => {
    const element = frameRef.current;
    if (!element) {
      return;
    }

    const update = () => {
      const stageHeightPx = element.clientHeight;
      setMetrics({
        stageHeightPx,
        scale: resolvePreviewStageViewportScale(stageHeightPx),
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [frameRef]);

  return metrics;
}
